/**
 * Feature landing page content registry.
 *
 * Single source of truth for the /features/* pages. Imported by the client page
 * template + nav dropdown and by the server SEO middleware + JSON-LD builder, so
 * page copy, meta tags, and structured data can never drift apart.
 */
import type { FeatureContent } from './types';
import { aiCandidateMatching } from './ai-candidate-matching';
import { aiCandidateProfiles } from './ai-candidate-profiles';
import { aiJobDescriptionGenerator } from './ai-job-description-generator';
import { interviewScheduling } from './interview-scheduling';
import { aiMockInterviews } from './ai-mock-interviews';
import { candidateFeedback } from './candidate-feedback';
import { aiInterviewNotes } from './ai-interview-notes';
import { jobDistribution } from './job-distribution';
import { matchInsights } from './match-insights';
import { upskillingRecommendations } from './upskilling-recommendations';
import { applicationTracking } from './application-tracking';
import { salaryNegotiation } from './salary-negotiation';

export * from './types';

/**
 * The one-line ATS reassurance rendered under the hero of every company
 * feature page where it is literally true (see `hero.worksWithAts`). Kept here
 * as a single constant so the sentence is identical on every page. Founder
 * copy, pasted verbatim, which is why it carries em dashes.
 */
export const WORKS_WITH_ATS_LINE =
  'Works with your ATS: candidates flow in, scorecards flow back.';

/** All 12 feature pages, in nav order (company features first, then candidate). */
export const FEATURE_CONTENT_LIST: FeatureContent[] = [
  aiCandidateMatching,
  aiCandidateProfiles,
  aiJobDescriptionGenerator,
  interviewScheduling,
  aiMockInterviews,
  candidateFeedback,
  aiInterviewNotes,
  jobDistribution,
  matchInsights,
  upskillingRecommendations,
  applicationTracking,
  salaryNegotiation,
];

/** Lookup by slug, for the dynamic /features/:slug route. */
export const FEATURE_CONTENT_BY_SLUG: Record<string, FeatureContent> = Object.fromEntries(
  FEATURE_CONTENT_LIST.map((feature) => [feature.slug, feature]),
);

/** The 8 company (B2B) feature pages, for the company header dropdown. */
export const COMPANY_FEATURES: FeatureContent[] = FEATURE_CONTENT_LIST.filter(
  (feature) => feature.audience === 'company',
);

/** The 4 candidate (B2C) feature pages, for the candidate header dropdown. */
export const CANDIDATE_FEATURES: FeatureContent[] = FEATURE_CONTENT_LIST.filter(
  (feature) => feature.audience === 'candidate',
);

/**
 * The five stages of the hiring mission, in order. Single source of truth for
 * the grouped company nav, the /how-it-works overview page, and the homepage
 * how-it-works section. The `stage` field on each company FeatureContent maps
 * to one of these ids. `body` holds the approved per-stage prose (design §3.4)
 * so every surface reads one source and cannot drift. No em dashes.
 */
export const MISSION_STAGES = [
  {
    id: 1,
    label: 'Define the role',
    body:
      'Talenture turns your hiring need into a job post and the criteria every later step matches against.',
  },
  {
    id: 2,
    label: 'Find and understand candidates',
    body:
      'Every applicant is read, structured, and ranked across eight signals, with a score you can defend.',
  },
  {
    id: 3,
    label: 'Prepare candidates',
    body:
      'Strong candidates practise on the real role and arrive ready. Everyone who does not progress gets honest feedback.',
  },
  {
    id: 4,
    label: 'Coordinate interviews',
    body:
      'Qualified candidates self-book a real slot from your live calendar across Google and Outlook.',
  },
  {
    id: 5,
    label: 'Capture the interview',
    body:
      'Every interview becomes a structured, decision-ready record, so the call is based on signal.',
  },
] as const;

export type MissionStage = (typeof MISSION_STAGES)[number];

/**
 * Company features grouped by mission stage, in stage order. Used by the nav
 * dropdown and the overview page so both read one source and cannot drift.
 */
export const COMPANY_FEATURES_BY_STAGE: { stage: MissionStage; features: FeatureContent[] }[] =
  MISSION_STAGES.map((stage) => ({
    stage,
    features: COMPANY_FEATURES.filter((feature) => feature.stage === stage.id),
  }));
